// Apolo IDE — useGitConnection (GitConnectionBar, Sprint github E1).
//
// Read-only assembly hook for the git/GitHub surface in the Code chat composer.
// It does NOT reimplement PR logic — it reuses:
//   - window.apolo.git.{currentBranch,originRemote,diff} (Electron IPC) for the
//     LOCAL state: current branch, origin owner/repo, and the +N/−M diff stat.
//   - the agent store's refreshPRs() / code.prs (backed by the Worker
//     /api/code/prs → githubApi.listUserPullRequests + enrichPullRequest +
//     mapCiStatus) for the repo's OPEN pull requests.
//
// Create-PR itself stays in useAutoPR/AutoPRButton — this hook only exposes a
// `refresh()` the bar calls after a PR is opened so branch/diff/PRs re-sync.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { computeDiffStat, formatDiffStat } from '../lib/gitDiffStat.js';
import { useAgentStore } from '../store/useAgentStore.js';

async function ipcCurrentBranch(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.currentBranch) return null;
  try { return await window.apolo.git.currentBranch({ root }); } catch { return null; }
}
async function ipcOriginRemote(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.originRemote) return null;
  try { return await window.apolo.git.originRemote({ root }); } catch { return null; }
}
async function ipcDiff(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.diff) return null;
  try { return await window.apolo.git.diff({ root }); } catch { return null; }
}

/**
 * @param {{ root?: string }} params worktree root (session.directory)
 */
export function useGitConnection({ root } = {}) {
  const electronOk = hasElectronBridge();
  const [phase, setPhase] = useState('idle'); // idle|loading|ready|error
  const [branch, setBranch] = useState(null);
  const [origin, setOrigin] = useState(null); // { url, owner, repo }
  const [diffStat, setDiffStat] = useState(null); // { additions, deletions, label }
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // PRs come from the existing store path (Worker /api/code/prs) — reuse it,
  // don't refetch GitHub here.
  const allPrs = useAgentStore((s) => s.code.prs);
  const prsSource = useAgentStore((s) => s.code.prsSource);
  const refreshPRs = useAgentStore((s) => s.refreshPRs);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!electronOk || !root) {
      setPhase(electronOk ? 'ready' : 'idle');
      return;
    }
    setPhase('loading');
    setError(null);
    const [branchRes, originRes, diffRes] = await Promise.all([
      ipcCurrentBranch(root),
      ipcOriginRemote(root),
      ipcDiff(root),
    ]);
    if (!mountedRef.current) return;
    if (branchRes?.ok) setBranch(branchRes.branch || null);
    if (originRes?.ok) {
      setOrigin({ url: originRes.url, owner: originRes.owner, repo: originRes.repo });
    } else {
      setOrigin(null);
    }
    if (diffRes?.ok) {
      setDiffStat(formatDiffStat(computeDiffStat(diffRes.diff || '')));
    } else {
      setDiffStat(null);
    }
    // A missing branch (e.g. not a repo) is the only hard error worth surfacing.
    if (!branchRes?.ok && !originRes?.ok) {
      setError(branchRes?.error || originRes?.detail || 'Não foi possível ler o git deste worktree.');
      setPhase('error');
      return;
    }
    setPhase('ready');
  }, [electronOk, root]);

  // Load on mount + whenever the worktree root changes.
  useEffect(() => { void load(); }, [load]);

  // Pull the global PR list once on mount so the repo filter has data.
  useEffect(() => { void refreshPRs(); }, [refreshPRs]);

  // Open PRs for THIS repo only. `origin` gives owner/repo; the store rows carry
  // `repo: "owner/repo"`. Falls back to ALL open PRs when origin is unknown
  // (graceful — better than empty).
  const repoSlug = useMemo(
    () => (origin?.owner && origin?.repo ? `${origin.owner}/${origin.repo}` : null),
    [origin],
  );
  const openPrs = useMemo(() => {
    const open = (allPrs || []).filter(
      (pr) => pr.status === 'draft' || pr.status === 'in_review',
    );
    if (!repoSlug) return open;
    const scoped = open.filter((pr) => pr.repo === repoSlug);
    // If nothing matches this repo, prefer an empty scoped list (honest) only
    // when we DO know the slug; the bar then collapses the PR section.
    return scoped;
  }, [allPrs, repoSlug]);

  // After a Create-PR (or manual refresh) re-sync branch/diff + PRs.
  const refresh = useCallback(async () => {
    await Promise.all([load(), refreshPRs()]);
  }, [load, refreshPRs]);

  // Connection state for the bar:
  //   no-electron → bar hidden (Electron-only feature)
  //   no-repo     → worktree without git
  //   not-connected → git ok but GitHub PRs are the mock fallback (no token)
  let connection = 'ready';
  if (!electronOk) connection = 'no-electron';
  else if (phase === 'loading') connection = 'loading';
  else if (phase === 'error') connection = 'error';
  else if (!root) connection = 'no-repo';
  else if (prsSource === 'mock' || prsSource === 'mock-fallback') connection = 'not-connected';

  return {
    electronOk,
    phase,
    connection,
    branch,
    origin,
    repoSlug,
    diffStat,
    openPrs,
    prsSource,
    error,
    refresh,
  };
}
